import React from 'react';
import ChartWheel from './ChartWheel';
import { generatePlacementInterpretation } from '../../lib/placementInterpretation';
import { generateAspectInterpretation, sortAspectsByImportance } from '../../lib/aspectInterpretation';
import {
  formatPlacementWithGlyph,
  formatAspectWithGlyph,
  getPlanetGlyph,
  getSignGlyph,
  SYMBOL_LEGEND,
} from '../../lib/chartGlyphs';
import {
  ordinalHouse,
  houseTheme,
  signTone,
  HOUSE_MEANINGS,
  SIGN_ORDER,
  generateHouseInterpretation,
} from '../../lib/reportUtils';
import { AMM_MAIN_SITE } from '../../constants/chartBrandLinks';

/**
 * Shared report content used by both /chart/report and /chart/report/print.
 * Ensures content parity — same data, same interpretation logic.
 */
const ReportContent = ({
  chartResult,
  meta,
  chart,
  wheelData,
  structure,
  sun,
  moon,
  rising,
  mc,
  variant = 'screen', // 'screen' | 'print'
}) => {
  const isPrint = variant === 'print';
  const baseClass = 'chart-report-document';
  const docClass = isPrint ? `${baseClass} chart-report-print-variant` : baseClass;

  const chartWheelFull = (
    <ChartWheel
      chart={wheelData}
      reportMode
      displayMode={false}
      dominantHouse={structure.houseEmphasis?.house}
      dominantSignIndex={structure.signEmphasis ? SIGN_ORDER.indexOf(structure.signEmphasis.sign) : -1}
    />
  );
  const chartWheelScreen = (
    <ChartWheel
      chart={wheelData}
      reportMode
      displayMode={false}
      dominantHouse={structure.houseEmphasis?.house}
      dominantSignIndex={structure.signEmphasis ? SIGN_ORDER.indexOf(structure.signEmphasis.sign) : -1}
    />
  );

  return (
    <article className={docClass}>
      <header className="chart-report-cover">
        <p className="chart-report-brand">Astral Moon Magic</p>
        <h1 className="chart-report-title">Natal Chart Report</h1>
        <p className="chart-report-subtitle">A guided reading of your natal chart</p>
        <div className="chart-report-meta">
          <p><strong>Birth date:</strong> {meta?.birthDate || '—'}</p>
          <p><strong>Birth time:</strong> {meta?.birthTime || '—'}</p>
          <p><strong>Location:</strong> {meta?.location || '—'}</p>
          <p><strong>Timezone:</strong> {chartResult.timezone || '—'} {chartResult.timezoneOffset || ''}</p>
          {chartResult.timeUnknown && <p className="chart-report-note">Time unknown — houses and angles are approximate.</p>}
        </div>
      </header>

      {isPrint ? (
        <>
          <section className="chart-report-chart-page" aria-label="Natal chart">
            <div className="chart-report-chart-display">
              {chartWheelFull}
            </div>
          </section>
          <section className="chart-report-intro">
            <p className="chart-report-intro-text">
              Your natal chart is a map of patterns — this report guides you through them.
              We look at how themes repeat, concentrate, and interact, rather than treating each symbol in isolation.
              The sections that follow move from overview to detail: structure first, then the deeper layers.
            </p>
          </section>
          <section className="chart-report-hero chart-report-hero-print">
            <div className="chart-report-hero-right">
              <h2 className="chart-report-hero-title">Chart at a glance</h2>
              <div className="chart-report-structure-grid">
                {structure.signEmphasis && (
                  <div className="chart-report-structure-item">
                    <span className="chart-report-structure-label">{structure.signEmphasis.sign} emphasis</span>
                    <span className="chart-report-structure-value">{structure.signEmphasis.count} placements</span>
                  </div>
                )}
                {structure.houseEmphasis && (
                  <div className="chart-report-structure-item">
                    <span className="chart-report-structure-label">{ordinalHouse(structure.houseEmphasis.house)} House emphasis</span>
                    <span className="chart-report-structure-value">{structure.houseEmphasis.planets.join(' • ')}</span>
                  </div>
                )}
                {structure.polarityAxes.map(({ signA, signB }) => (
                  <div key={`${signA}-${signB}`} className="chart-report-structure-item">
                    <span className="chart-report-structure-value">{signA} ↔ {signB} axis active</span>
                  </div>
                ))}
              </div>
              {structure.toneParagraph && (
                <p className="chart-report-tone">{structure.toneParagraph}</p>
              )}
              <div className="chart-report-big-three-compact">
                <h3 className="chart-report-big-three-compact-title">Big Three</h3>
                {sun && <p>{getPlanetGlyph('sun')} Sun — {getSignGlyph(sun.sign)} {sun.sign}</p>}
                {moon && <p>{getPlanetGlyph('moon')} Moon — {getSignGlyph(moon.sign)} {moon.sign}</p>}
                {rising && <p>Rising — {rising?.sign ? `${getSignGlyph(rising.sign)} ${rising.sign}` : '—'}</p>}
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          <section className="chart-report-intro">
            <p className="chart-report-intro-text">
              Your natal chart is a map of patterns — this report guides you through them.
              We look at how themes repeat, concentrate, and interact, rather than treating each symbol in isolation.
              The sections that follow move from overview to detail: structure first, then the deeper layers.
            </p>
          </section>
          <section className="chart-report-hero">
            <div className="chart-report-hero-left">
              <div className="chart-report-wheel-wrap chart-report-hero-wheel">
                {chartWheelFull}
              </div>
            </div>
            <div className="chart-report-hero-right">
              <h2 className="chart-report-hero-title">Chart at a glance</h2>
              <div className="chart-report-structure-grid">
                {structure.signEmphasis && (
                  <div className="chart-report-structure-item">
                    <span className="chart-report-structure-label">{structure.signEmphasis.sign} emphasis</span>
                    <span className="chart-report-structure-value">{structure.signEmphasis.count} placements</span>
                  </div>
                )}
                {structure.houseEmphasis && (
                  <div className="chart-report-structure-item">
                    <span className="chart-report-structure-label">{ordinalHouse(structure.houseEmphasis.house)} House emphasis</span>
                    <span className="chart-report-structure-value">{structure.houseEmphasis.planets.join(' • ')}</span>
                  </div>
                )}
                {structure.polarityAxes.map(({ signA, signB }) => (
                  <div key={`${signA}-${signB}`} className="chart-report-structure-item">
                    <span className="chart-report-structure-value">{signA} ↔ {signB} axis active</span>
                  </div>
                ))}
              </div>
              {structure.toneParagraph && (
                <p className="chart-report-tone">{structure.toneParagraph}</p>
              )}
              <div className="chart-report-big-three-compact">
                <h3 className="chart-report-big-three-compact-title">Big Three</h3>
                {sun && <p>{getPlanetGlyph('sun')} Sun — {getSignGlyph(sun.sign)} {sun.sign}</p>}
                {moon && <p>{getPlanetGlyph('moon')} Moon — {getSignGlyph(moon.sign)} {moon.sign}</p>}
                {rising && <p>Rising — {rising?.sign ? `${getSignGlyph(rising.sign)} ${rising.sign}` : '—'}</p>}
              </div>
            </div>
          </section>
        </>
      )}

      <section className="chart-report-section chart-report-symbol-legend">
        <h2>Understanding the Symbols</h2>
        <p className="chart-report-lead">
          A quick reference for the symbols used in this report. Each planet, sign, and aspect type carries meaning.
        </p>
        <div className="chart-report-legend-grid">
          <div className="chart-report-legend-block">
            <h4>Planets</h4>
            <ul className="chart-report-legend-list">
              {SYMBOL_LEGEND.planets.map((item) => (
                <li key={item.name}><span className="chart-report-legend-glyph">{item.glyph}</span> {item.name} — {item.desc}</li>
              ))}
            </ul>
          </div>
          <div className="chart-report-legend-block">
            <h4>Signs</h4>
            <ul className="chart-report-legend-list">
              {SYMBOL_LEGEND.signs.map((item) => (
                <li key={item.name}><span className="chart-report-legend-glyph">{item.glyph}</span> {item.name} — {item.desc}</li>
              ))}
            </ul>
          </div>
          <div className="chart-report-legend-block">
            <h4>Aspects</h4>
            <ul className="chart-report-legend-list">
              {SYMBOL_LEGEND.aspects.map((item) => (
                <li key={item.name}><span className="chart-report-legend-glyph">{item.glyph}</span> {item.name} — {item.desc}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="chart-report-section">
        <h2>Big Three & orientation</h2>
        <p className="chart-report-lead">
          Sun, Moon, and Rising are the best starting points for any chart — they orient identity, emotional style, and first approach.
        </p>
        <div className="chart-report-big-three">
          {sun && <p><strong>{getPlanetGlyph('sun')} Sun in {getSignGlyph(sun.sign)} {sun.sign}</strong> — Core identity and creative will. How you organize your sense of self.</p>}
          {moon && <p><strong>{getPlanetGlyph('moon')} Moon in {getSignGlyph(moon.sign)} {moon.sign}</strong> — Emotional needs and instinctive response. How you seek safety and restore yourself.</p>}
          {rising && <p><strong>Rising {rising.sign ? `${getSignGlyph(rising.sign)} ${rising.sign}` : '—'}</strong> — First approach and embodiment. How new cycles begin for you.</p>}
          {mc && <p><strong>Midheaven {mc.sign ? `${getSignGlyph(mc.sign)} ${mc.sign}` : '—'}</strong> — Direction and public contribution. Where vocation and ambition take shape.</p>}
        </div>
      </section>

      <section className="chart-report-section">
        <h2>Core themes</h2>
        <p className="chart-report-lead">Main patterns emerging from the chart — what stands out before the details.</p>
        {structure.coreThemes.length > 0 ? (
          <>
            <ul className="chart-report-themes-list">
              {structure.coreThemes.map((item, i) => (
                <li key={i}>
                  <strong>{item.label}</strong> — {item.theme}
                </li>
              ))}
            </ul>
            {structure.synthesisThemes?.length > 0 && (
              <p className="chart-report-synthesis">
                Across the chart: {structure.synthesisThemes.join('; ')}. These patterns repeat in placements and aspects below.
              </p>
            )}
            <p className="chart-report-transition">
              These themes will appear again in the placement and house sections below. Notice what repeats.
            </p>
          </>
        ) : (
          <p className="chart-report-transition">
            The Big Three and placements below reveal the chart&apos;s main patterns. Start with Sun, Moon, and Rising.
          </p>
        )}
      </section>

      <section className="chart-report-section chart-report-section-wide">
        <h2>Planetary placements</h2>
        <p className="chart-report-lead">
          Each placement describes a function, expressed through sign and house. These are the deeper layers of the story outlined above.
        </p>
        {chart.planets
          .filter((p) => ['sun', 'moon', 'mercury', 'venus', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto', 'northnode', 'lilith', 'chiron'].includes(p.key))
          .map((placement) => {
            const interp = generatePlacementInterpretation({ placement });
            if (!interp) return null;
            return (
              <div key={placement.key} className="chart-report-placement">
                <h3>{formatPlacementWithGlyph(placement, ordinalHouse)}</h3>
                <p className="chart-report-core">{interp.corePattern}</p>
                <ul>
                  {interp.possibleExpressions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="chart-report-observation">{interp.observation}</p>
              </div>
            );
          })}
      </section>

      <section className="chart-report-section chart-report-section-wide">
        <h2>Houses</h2>
        <p className="chart-report-lead">
          Houses show where the chart&apos;s energy becomes visible in life. The cusp sign colours each arena; planets in a house activate it.
        </p>
        {chart.houses?.map((house) => {
          const planetsInHouse = chart.planets.filter((p) => Number(p.house) === Number(house.house));
          const h = ordinalHouse(house.house);
          const theme = HOUSE_MEANINGS[house.house] || 'Life arena';
          const houseText = generateHouseInterpretation(house, planetsInHouse);
          return (
            <div key={house.house} className="chart-report-house">
              <h3>{h} House — {theme}</h3>
              <p>{houseText}</p>
            </div>
          );
        })}
      </section>

      <section className="chart-report-section chart-report-analysis-chart-section">
        <h2>Chart with aspects</h2>
        <p className="chart-report-lead">
          The chart below shows major aspects between planets — useful for the aspect interpretations that follow.
        </p>
        <div className="chart-report-analysis-chart-wrap">
          {chartWheelScreen}
        </div>
      </section>
      <section className="chart-report-section chart-report-section-wide chart-report-major-aspects">
        <h2>Major aspects</h2>
        <p className="chart-report-lead">
          Aspects show how key functions interact — flow, tension, or dialogue. Tighter orbs tend to be more pronounced.
          {structure.synthesisThemes?.length > 0 && ' Notice which themes repeat across the aspects below.'}
        </p>
        {sortAspectsByImportance(chartResult.aspects || [], chart).map((aspect) => {
          const p1 = chart.planets.find((p) => p.key === (aspect.p1 || '').toLowerCase());
          const p2 = chart.planets.find((p) => p.key === (aspect.p2 || '').toLowerCase());
          const interp = generateAspectInterpretation({ aspect, p1, p2 });
          if (!interp) return null;
          const aspectGlyphLine = formatAspectWithGlyph(aspect.p1, aspect.p2, aspect.type, aspect.p1Label, aspect.p2Label);
          return (
            <div key={aspect.id} className="chart-report-aspect">
              <h3 className="chart-report-aspect-glyph">{aspectGlyphLine}</h3>
              <h4 className="chart-report-aspect-title">{interp.title}</h4>
              {interp.paragraphs.map((para, i) => (
                <p key={i} className="chart-report-aspect-para">{para}</p>
              ))}
            </div>
          );
        })}
      </section>

      <footer className="chart-report-closing">
        <h2>Closing integration</h2>
        <p>
          The themes from the chart at a glance run through the placements and aspects above. What repeats — by sign, house, or aspect — tends to show up in how you approach identity, relationships, and creative expression.
          {structure.synthesisThemes?.length > 0 && ` Patterns such as ${structure.synthesisThemes.slice(0, 2).join(' and ')} suggest where the chart is asking for integration.`}
        </p>
        <p>
          This report offers a structured view, not fixed predictions. Notice what resonates and what invites curiosity.
          Your chart is a map of potential; how you move through it is yours to shape.
        </p>
        <p className="chart-report-cta">
          <a href="/chart">Explore the interactive chart</a>
          {' '}
          at{' '}
          <a href={AMM_MAIN_SITE} target="_blank" rel="noopener noreferrer">
            Astral Moon Magic
          </a>
          {' '}
          to deepen your understanding.
        </p>
      </footer>
    </article>
  );
};

export default ReportContent;
